import { PermissionEnum } from '@creditsights/api-ui-web';
import {
  Loader,
  Button,
  ButtonMode,
  ButtonSize,
  Icon,
  ICONS,
  Divider,
  agTableGetColumns,
  useGlobalModalContext,
  colors,
  DataLoadError,
  Dropdown,
} from '@creditsights/styleguide-web';
import {
  GridApi,
  GridReadyEvent,
  ColumnApi,
  GetRowIdFunc,
  ColDef,
  IServerSideDatasource,
} from '@ag-grid-community/core';
import classNames from 'classnames/bind';
import isEmpty from 'lodash/isEmpty';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { DropdownItemProps } from 'semantic-ui-react';
import { EXPORT_EXCEL_SHEET_NAME } from '../../Base.constants';
import { Coverage } from '../../components/Filters/Filters.constants';
import IssuersQuickFiltersRow from '../../components/Filters/IssuersQuickFiltersRow';
import { MODAL_TYPES } from '../../components/GlobalModal';
import { ManageColumnsModalProps } from '../../components/widgets/Dashboard/ManageColumnsModal/ManageColumnsModal';
import useIssuersFilterContext from '../../hooks/filters/useIssuersFilterContext';
import {
  isIssuersFilterApplied,
  // validateFilters,
} from '../../hooks/filters/useIssuersFilterContext.logic';
// import { useFetchIssuers } from '../../hooks/issuers';
import useFetchUserPermissions from '../../hooks/permissions/useFetchUserPermissions';
import { queryKeys } from '../../hooks/queryKeyFactory';
import useFetchRpColumns from '../../hooks/riskProducts/useFetchRpColumns';
// import useTableOverlayHandler from '../../hooks/table/useTableOverlayHandler';
import useTableQueryParams from '../../hooks/table/useTableQueryParams';
import { useFetchWatchLists } from '../../hooks/watchLists';
import { WatchList } from '../../models/WatchList';
import { IssuerListDto, IssuerListItemDto } from '../../services/dtos/issuer/issuerList.dto';
import { EsField } from '../../services/dtos/riskProducts/rpColumn.dto';
import { fetchIssuers } from '../../services/issuers/issuers.service';
import { getGroupedFilterTags, getRPFilterParams } from '../../utils/filterUtils';
import { hasPermissionToExportAllFields, hasPermissionToRp } from '../../utils/permissionsUtils';
import { sortWatchlists } from '../../utils/watchListsUtils';
import { ISSUERS_LIMIT_PER_WATCHLIST } from '../MyContent/WatchlistsTab/Watchlist/Watchlist.logic';
import AddToWatchlistMenu from './AddToWatchlistMenu';
import {
  additionalDefaultColDef,
  COLUMN_PERSISTENCE_KEY,
  COMPANY_DETAILS_COLUMNS_TO_EXPORT,
  COVERAGE_ANDOR_PERMISSION,
  DEFAULT_SORT_FIELD,
  DEFAULT_SORT_ORDER,
  GRID_PAGE_SIZE,
  RISK_PRODUCT_PREFIX,
  RP_COLUMNS_TO_EXPORT,
} from './Company.constants';
import Table from '../../components/FullPageTable';
import styles from './Companies.module.scss';
import {
  createExportFileName,
  getColDefForColumns,
  getInitialCompanyColumns,
  mapRpColumnToDefinition,
  mapSortField,
  prepareRPColumnsForCompany,
  processCellForExport,
} from './Companies.logic';
import SelectColumn from './SelectColumn';

const cx = classNames.bind(styles);

const CompanyTable = Table<IssuerListItemDto>;

const getDropdownOptions = (options: WatchList[]): DropdownItemProps[] =>
  options.map((option) => ({
    text: option.name,
    value: option.id,
    key: option.id,
    'data-testid': `watchlist-${option}`,
  }));

const Companies = (): React.ReactElement | null => {
  const { showModal, setProps } = useGlobalModalContext();
  const queryClient = useQueryClient();
  const { data: userPermissions } = useFetchUserPermissions();
  const { data: rpColumnsInit, isLoading: isRpColumnsLoading } = useFetchRpColumns(!hasPermissionToRp(userPermissions));
  const rpColumns = useMemo(() => prepareRPColumnsForCompany(rpColumnsInit || []), [rpColumnsInit, userPermissions]);

  const gridApiRef = useRef<GridApi | null>(null);
  const columnApiRef = useRef<ColumnApi | null>(null);
  const allColumns = useMemo(
    () =>
      rpColumns.map((column) => {
        const def = mapRpColumnToDefinition(column);
        return {
          ...def,
          hide:
            def.colId === 'latestRecommendation.weighting'
              ? !userPermissions?.includes(PermissionEnum.RESEARCH)
              : def.hide,
        };
      }),
    [rpColumns, userPermissions]
  );

  const [initialColumnDefs, setColumnDefs] = useState<ColDef<IssuerListItemDto>[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const [selectedAll, setSelectedAll] = useState(false);
  const [selectAllIsLoading, setSelectAllIsLoading] = useState(false);
  const [exportIsLoading, setExportIsLoading] = useState(false);
  const [sortField, setSortField] = useState(DEFAULT_SORT_FIELD);
  const [sortOrderAsc, setSortOrderAsc] = useState(DEFAULT_SORT_ORDER !== 'desc');
  const { setWatchlistId, setCoverage, activeFilters } = useIssuersFilterContext();
  // const isFilterValid = useMemo(() => validateFilters(activeFilters), [activeFilters]);
  const isAnyFilterApplied = useMemo(() => isIssuersFilterApplied(activeFilters), [activeFilters]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>();
  const [isError, setIsError] = useState(false);
  const [invalidate, setInvalidate] = useState(false);
  const [totalResults, setTotalResults] = useState<number>();
  const coverageAndOrEnabled = !!userPermissions?.permissions.includes(COVERAGE_ANDOR_PERMISSION);

  useEffect(() => {
    if (!activeFilters.coverage || activeFilters.coverage.length === 0) {
      const defaultCoverageSelection: string[] = [];

      if (userPermissions?.includes(PermissionEnum.RESEARCH)) {
        defaultCoverageSelection.push(Coverage.CS);
      }
      if (userPermissions?.includes(PermissionEnum.COVENANTS)) {
        defaultCoverageSelection.push(Coverage.CR);
      }
      if (userPermissions?.includes(PermissionEnum.NEWS)) {
        defaultCoverageSelection.push(Coverage.LFI);
      }
      if (userPermissions?.includes(PermissionEnum.FINANCIALS)) {
        defaultCoverageSelection.push(Coverage.Financials);
      }
      if (userPermissions?.hasAccessToRP()) {
        defaultCoverageSelection.push(Coverage.RiskProducts);
      }

      setCoverage(defaultCoverageSelection);
    }
  }, [userPermissions]);

  const { sort, onSortChanged } = useTableQueryParams();
  const colDefSort = sort.sortOrderAsc ? 'asc' : 'desc';

  useEffect(() => {
    const initialCols = getInitialCompanyColumns(allColumns, userPermissions);
    const colDefWithRp = agTableGetColumns<IssuerListItemDto>(COLUMN_PERSISTENCE_KEY, initialCols, allColumns, false);
    setColumnDefs(colDefWithRp);
  }, [allColumns]);

  const columnDefs = useMemo(
    () =>
      initialColumnDefs.map((col) => ({
        ...(col.colId === 'selection'
          ? {
              ...col,
              cellRenderer: SelectColumn,
              cellRendererParams: {
                selectedAll,
                selected: selectedCompanies,
                onSelect: setSelectedCompanies,
                onSelectAll: setSelectedAll,
              },
              headerComponent: SelectColumn,
              headerComponentParams: {
                selectedAll,
                selected: selectedCompanies,
                onSelect: setSelectedCompanies,
                onSelectAll: setSelectedAll,
                resultsLimit: ISSUERS_LIMIT_PER_WATCHLIST,
                totalResults,
              },
              checkboxSelection: undefined,
              headerClass: undefined,
              cellClass: undefined,
              maxWidth: 35,
            }
          : col),
        sort: col.colId === sort.sortField ? colDefSort : col.sort,
      })),
    [initialColumnDefs, selectedCompanies, selectedAll, totalResults]
  );

  const {
    data: watchLists,
    isLoading: isWatchListsLoading,
    isError: isWatchListsError,
  } = useFetchWatchLists({ select: (data) => sortWatchlists(data) });

  const manageColumnsProps: ManageColumnsModalProps = useMemo(
    () => ({
      rpColumns,
      checkedColumns: columnDefs.map((c) => c.field as string),
      setCheckedColumns: (esFields: EsField[]) => {
        const newDefs = getColDefForColumns(esFields, rpColumns);
        setColumnDefs(newDefs);
      },
      resetColumns: () => {
        setColumnDefs(getInitialCompanyColumns(allColumns, userPermissions));
      },
      onClose: () => {
        setIsModalOpen(false);
      },
    }),
    [rpColumns, columnDefs]
  );

  useEffect(() => {
    if (isModalOpen) {
      setProps(MODAL_TYPES.MANAGE_COLUMNS, manageColumnsProps);
    }
  }, [manageColumnsProps, isModalOpen]);

  const onManageColumns = () => {
    setIsModalOpen(true);
    showModal(MODAL_TYPES.MANAGE_COLUMNS, manageColumnsProps);
  };

  const calcQueryParams = (skip?: number, take?: number, sortField?: string, sortOrderAsc?: boolean) => ({
    skip,
    take,
    tags: getGroupedFilterTags(activeFilters),
    recommendationWeightings: activeFilters.recWeightings,
    text: activeFilters.searchText,
    coverage: activeFilters.coverage || [],
    coverageOperation: coverageAndOrEnabled ? activeFilters.coverageOperation || 'or' : 'or',
    rpFilters: getRPFilterParams(activeFilters),
    watchlistId: activeFilters.watchlistId,
    sortField,
    sortOrderAsc,
  });

  const prefetchAll = async () => {
    if (totalResults) {
      const pageSize = 1000;
      const numberOfFetches = Math.ceil(Math.min(10000, totalResults) / pageSize);
      const results: IssuerListDto[] = [];
      for (let i = 0; i < numberOfFetches; i += 1) {
        const payload = calcQueryParams(pageSize * i, pageSize, sortField, sortOrderAsc);
        // eslint-disable-next-line no-await-in-loop
        const result = await queryClient.fetchQuery<IssuerListDto, Error>(queryKeys.fetchIssuers.query(payload), () =>
          fetchIssuers(payload)
        );

        results.push(result);
      }

      const rows = results.reduce((acc: IssuerListItemDto[], res) => [...acc, ...res.results], []);

      gridApiRef.current?.applyServerSideTransaction({
        add: rows,
      });

      return rows;
    }

    return [];
  };

  useEffect(() => {
    if (selectedAll) {
      if (totalResults) {
        setSelectAllIsLoading(true);

        (async () => {
          try {
            const rows = await prefetchAll();
            setSelectedCompanies(rows.map((item) => item.tagId));
          } catch (e: any) {
            setSelectedAll(false);
            setSelectedCompanies([]);
          }

          setSelectAllIsLoading(false);
        })();
      }
    } else {
      setSelectedCompanies(
        totalResults && selectedCompanies.length > 0 && selectedCompanies.length < totalResults ? selectedCompanies : []
      );
    }
  }, [selectedAll, totalResults, sortField, sortOrderAsc]);

  const datasource = useMemo<IServerSideDatasource>(
    () => ({
      getRows: async (params) => {
        const { request, success, fail } = params;

        const skip = request.startRow;
        const take = request.endRow && request.startRow !== undefined ? request.endRow - request.startRow : undefined;
        const sortField = mapSortField(request.sortModel[0]?.colId || DEFAULT_SORT_FIELD);
        const sortOrderAsc = (request.sortModel[0]?.sort ?? DEFAULT_SORT_ORDER) !== 'desc';
        const queryParams = calcQueryParams(skip, take, sortField, sortOrderAsc);

        if (!skip) {
          gridApiRef.current?.showLoadingOverlay?.();
        }

        try {
          const { results, totalResults } = await queryClient.fetchQuery(
            queryKeys.fetchIssuers.query(queryParams),
            () => fetchIssuers(queryParams)
          );

          if (!skip) {
            setSelectedCompanies([]);
            setSelectedAll(false);
            setTotalResults(totalResults);
            setSortField(sortField);
            setSortOrderAsc(sortOrderAsc);

            if (results.length === 0) {
              gridApiRef.current?.showNoRowsOverlay?.();
            } else {
              gridApiRef.current?.hideOverlay?.();
            }
          }

          success({
            rowData: results,
            rowCount: Math.min(10000, totalResults),
          });
        } catch (e: any) {
          fail();
          setIsError(true);
        }
      },
    }),
    [activeFilters, invalidate]
  );

  const onGridReady = useCallback((event: GridReadyEvent) => {
    gridApiRef.current = event.api;
    columnApiRef.current = event.columnApi;
    event.api.showLoadingOverlay();

    setInvalidate((cur) => !cur);
  }, []);

  useEffect(() => {
    gridApiRef.current?.setServerSideDatasource(datasource);
  }, [datasource]);

  const exportDataToExcel = async () => {
    setExportIsLoading(true);
    await prefetchAll();

    const exportAllFields = hasPermissionToExportAllFields(userPermissions);
    const hasRP3Permissions = userPermissions?.permissions.includes(PermissionEnum.RISK_PRODUCTS_ENTITLEMENT_LEVEL_3);
    const hasCQCRFAPermission = userPermissions?.permissions.includes('ui:export:companies_with_cq_cr_fa');

    const columnsToExport = [
      ...COMPANY_DETAILS_COLUMNS_TO_EXPORT,
      ...RP_COLUMNS_TO_EXPORT.map((fieldName) => `${RISK_PRODUCT_PREFIX}.${fieldName}`),
    ];

    const columnKeys = gridApiRef.current
      ?.getColumnDefs()
      ?.filter((col: ColDef) => col.colId !== 'selection' && !col.hide)
      .map((col: ColDef) => col.colId ?? '')
      .filter((colId) => columnsToExport.includes(colId))
      .filter(
        (colId) =>
          userPermissions?.permissions.includes(PermissionEnum.RESEARCH) || !colId.startsWith('latestRecommendation.')
      )
      // ?.filter((colId) => !hasRP3Permissions || !hasCQCRFAPermission || colId !== `${RISK_PRODUCT_PREFIX}.acr_level_4`)
      ?.filter(
        (colId) => hasRP3Permissions || ![`${RISK_PRODUCT_PREFIX}.cqs`, `${RISK_PRODUCT_PREFIX}.acr`].includes(colId)
      );

    gridApiRef.current?.exportDataAsExcel({
      processCellCallback: (params) => {
        if (!userPermissions?.permissions.includes('ui:export:companies_with_cs')) {
          if (params.column.getColId().startsWith('latestRecommendation.')) {
            return 'Update your subscription to view this info';
          }
        }

        if (!hasCQCRFAPermission) {
          if (
            params.column.getColId() === `${RISK_PRODUCT_PREFIX}.cqs` ||
            params.column.getColId() === `${RISK_PRODUCT_PREFIX}.fas` ||
            params.column.getColId() === `${RISK_PRODUCT_PREFIX}.cre_1y`
          ) {
            return 'Update your subscription to view this info';
          }
        }

        if (
          (!exportAllFields || !hasCQCRFAPermission || !hasRP3Permissions) &&
          params.column.getColId() === `${RISK_PRODUCT_PREFIX}.acr`
        ) {
          return '';
        }

        return processCellForExport(params);
      },
      fileName: createExportFileName(),
      sheetName: EXPORT_EXCEL_SHEET_NAME,
      columnKeys,
    });

    setExportIsLoading(false);
  };

  const renderMainHeader = () => (
    <>
      <div className={cx('main-header-title')}>Companies</div>
      <div className={cx('main-header-content')}>
        <IssuersQuickFiltersRow coverageAndOrEnabled={coverageAndOrEnabled} />
        {hasPermissionToRp(userPermissions) && (
          <div className={cx('main-header-right')}>
            <Button
              mode={ButtonMode.SECONDARY}
              size={ButtonSize.SMALL}
              onClick={() => showModal(MODAL_TYPES.FILTERS)}
              dataTestId="filters-button"
              className={cx({ 'with-filters-indicator': isAnyFilterApplied })}
            >
              <Icon name={ICONS.Filters} width={12} height={12} />
              More Filters
            </Button>
          </div>
        )}
      </div>
    </>
  );

  const renderTableHeader = () => (
    <h2 className={cx('header')}>
      <div className={cx('header-left')}>
        <span>
          Results
          <span className={cx('header-results')}>{totalResults?.toLocaleString('en-US')}</span>
        </span>
      </div>
      <div className={cx('header-right')}>
        {hasPermissionToRp(userPermissions) && (
          <Button
            mode={ButtonMode.SECONDARY}
            size={ButtonSize.SMALL}
            onClick={onManageColumns}
            dataTestId="manage-columns-button"
          >
            Columns
            <Icon name={ICONS.Columns} width={12} height={12} color={colors['white-color']} />
          </Button>
        )}
        <Dropdown
          onOptionSelect={(watchlistId) => {
            if ((activeFilters.watchlistId || '') !== watchlistId) {
              setWatchlistId(watchlistId as string);
            }
          }}
          options={getDropdownOptions(watchLists ?? [])}
          value={activeFilters.watchlistId}
          dataTestId="watchlist-select"
          isLeftAligned
          withCaretIcon
          disabled={isWatchListsLoading || isWatchListsError || isEmpty(watchLists)}
          withCheckmarks
          small
          placeholder="Load watchlist"
        />
        <Divider vertical className={cx('header-divider')} />
        <span>
          <Button
            mode={ButtonMode.SECONDARY}
            size={ButtonSize.SMALL}
            onClick={exportDataToExcel}
            dataTestId="export-table-button"
            disabled={!totalResults || exportIsLoading}
            loading={exportIsLoading}
          >
            <Icon name={ICONS.Download} width={12} height={12} />
            Export
          </Button>
        </span>
        <AddToWatchlistMenu
          companies={selectedCompanies}
          popupClassName={cx('add-to-watchlist-popup')}
          hidePopupOnScroll
          isLoading={selectAllIsLoading}
        />
      </div>
    </h2>
  );

  const rowIdResolver = useCallback<GetRowIdFunc<IssuerListItemDto>>((params) => params.data.tagId, []);

  if (isRpColumnsLoading) {
    return (
      <div className={cx('loader-container')}>
        <Loader />
      </div>
    );
  }

  const renderContent = () => {
    if (isError) {
      return (
        <div className={cx('state-wrapper')}>
          <DataLoadError
            onRetryClick={() => {
              setIsError(false);
              setInvalidate(!invalidate);
            }}
          />
        </div>
      );
    }

    return (
      <CompanyTable
        pageTitle={renderMainHeader()}
        headerComponent={renderTableHeader()}
        columnPersistenceKey={COLUMN_PERSISTENCE_KEY}
        onGridReady={onGridReady}
        rowModelType="serverSide"
        getRowId={rowIdResolver}
        columnDefs={columnDefs}
        onSortChanged={onSortChanged}
        suppressRowClickSelection
        loadingOverlayComponent={Loader}
        overlayNoRowsTemplate="No results found"
        additionalDefaultColDef={additionalDefaultColDef}
        suppressRowHoverHighlight={false}
        cacheBlockSize={GRID_PAGE_SIZE}
      />
    );
  };

  return <div className={cx('body')}>{renderContent()}</div>;
};

export default Companies;
